import type {
  Cohort,
  CohortFilter,
  MerchantSignalRow,
  SignalsPlan,
  TermSplit,
} from "./types";

/** Applies a cohort filter. Unset keys are ignored, so filters compose. */
export function matchesCohort(
  row: MerchantSignalRow,
  filter: CohortFilter,
): boolean {
  if (filter.segment !== undefined && row.segment !== filter.segment) {
    return false;
  }
  if (filter.tier !== undefined && row.tier !== filter.tier) return false;
  if (filter.country !== undefined && row.country !== filter.country) {
    return false;
  }
  if (filter.channel !== undefined && row.channel !== filter.channel) {
    return false;
  }
  if (
    filter.businessShape !== undefined &&
    row.businessShape !== filter.businessShape
  ) {
    return false;
  }
  if (filter.mccCode !== undefined && row.mccCode !== filter.mccCode) {
    return false;
  }
  if (filter.stallStage !== undefined && row.stallStage !== filter.stallStage) {
    return false;
  }
  if (filter.activated !== undefined && row.activated !== filter.activated) {
    return false;
  }
  if (filter.plan !== undefined && row.plan !== filter.plan) return false;
  if (
    filter.signupMonth !== undefined &&
    (row.signupAt ?? "").slice(0, 7) !== filter.signupMonth
  ) {
    return false;
  }
  if (filter.businessIds && !filter.businessIds.includes(row.businessId)) {
    return false;
  }
  return true;
}

export function selectCohort(
  rows: MerchantSignalRow[],
  filter: CohortFilter,
): MerchantSignalRow[] {
  return rows.filter((row) => matchesCohort(row, filter));
}

/** The drill-through behind a plan count on the dashboard. */
export function planCohort(plan: SignalsPlan): Cohort {
  return { label: plan === "pro" ? "Pro plan" : "Free plan", filter: { plan } };
}

/**
 * Counts the Pro rows by billing term. A Pro row with no resolvable term counts
 * as `unknown` rather than `monthly`, so the three always sum to the Pro rows.
 */
export function termSplit(rows: MerchantSignalRow[]): TermSplit {
  const split: TermSplit = { monthly: 0, annual: 0, unknown: 0 };
  for (const row of rows) {
    if (row.plan !== "pro") continue;
    if (row.subscriptionTerm === "monthly") split.monthly += 1;
    else if (row.subscriptionTerm === "annual") split.annual += 1;
    else split.unknown += 1;
  }
  return split;
}

const CSV_COLUMNS = [
  "businessId",
  "segmentLabel",
  "tier",
  "mccCode",
  "country",
  "businessShape",
  "signupAt",
  "activated",
  "firstTxnAt",
  "lastTxnAt",
  "daysToFirstTxn",
  "txnCount",
  "gpvCents",
  "currency",
  "gpvBucket",
  "avgTicketBucket",
  "overallStage",
  "stallStage",
  "identityStage",
  "subscriptionStage",
  "plan",
  "subscriptionTerm",
  "channel",
  "utmSource",
  "utmMedium",
  "wasReferred",
  "usesTerminal",
  "outgrewSolo",
] as const satisfies readonly (keyof MerchantSignalRow)[];

/**
 * Escapes a value for CSV. Quotes anything containing a delimiter, quote, or
 * newline; a leading `=`, `+`, `-` or `@` is prefixed with a quote so
 * spreadsheets treat it as text rather than a formula.
 */
function toCsvValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  const raw = String(value);
  const safe = /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function toCsv(rows: MerchantSignalRow[]): string {
  const lines = [CSV_COLUMNS.join(",")];
  for (const row of rows) {
    lines.push(
      CSV_COLUMNS.map((key) =>
        // eslint-disable-next-line security/detect-object-injection -- key comes from the CSV_COLUMNS literal, not from input
        toCsvValue(row[key]),
      ).join(","),
    );
  }
  return lines.join("\n");
}

/** Slugifies a cohort label into a filename stem. */
export function toCsvFilename(label: string, generatedAt: string): string {
  const slug =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "cohort";
  return `signals-${slug}-${generatedAt.slice(0, 10)}.csv`;
}
